<template>
  <div class="container-fluid p-0">
    <div v-if="currentOrder">

      <div class="d-flex gap-3 align-items-center p-2">
        <RouterLink :to="TranslationsService.i18nRoute({ name: 'orders' })" class="btn btn-outline-secondary d-none d-lg-inline-block" active-class="">
          <font-awesome-icon :icon="['fas', 'chevron-left']" />
          {{ $t('global.list') }}
        </RouterLink>
        <div>
          <RouterLink :to="TranslationsService.i18nRoute({ name: 'orders' })" class="btn btn-outline-secondary d-lg-none" active-class="">
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
            {{ $t('global.list') }}
          </RouterLink><br class="d-lg-none">
          <span class="text-primary fw-bold fs-3">{{ currentOrder.code }}</span><br>
          <span class="text-melodi">{{ currentSupplier?.name }}</span><br>
        </div>
        <div>
          <font-awesome-icon :icon="['fas', 'shopping-cart']" class="me-2" />
          <OrderStatus :status="currentOrder.status" />
          <br>
          <font-awesome-icon :icon="['fas', 'envelope']" class="me-2" />
          <EmailQueueStatus :status="emailQueue?.status ?? 4" :title="emailQueueStatusTitle" />
          <template v-if="currentOrder.deliveryDate && currentOrder.status !== ORDER_HEAD_STATUS.cancelled">
          <br>
          <font-awesome-icon :icon="['fas', 'truck']" class="me-2" />
          <span>{{ $d(new Date(currentOrder.deliveryDate), 'dateonly') }}</span>
          </template>
        </div>
        <div class="d-none d-lg-block">
          {{ $t('field.generic.created') }}<br>
          <AuditItem :at="currentOrder.audit.createdAt" :by="currentOrder.audit.createdBy" />
        </div>
        <div v-if="currentOrder.audit.createdAt !== currentOrder.audit.updatedAt" class="d-none d-xxl-block">
          {{ $t('field.generic.updated') }}<br>
          <AuditItem :at="currentOrder.audit.updatedAt" :by="currentOrder.audit.updatedBy" />
        </div>
        <div class="btn-group d-none d-lg-block">
          <button v-if="currentOrder.status === ORDER_HEAD_STATUS.pending || currentOrder.status === ORDER_HEAD_STATUS.inProgress" @click="cancelOrder" class="btn btn-outline-danger">
            <font-awesome-icon :icon="['fas', 'xmark']" />
            {{ $t('action.cancelOrderShort') }}
          </button>
          <button v-if="currentOrder.status === ORDER_HEAD_STATUS.pending && (!emailQueue || emailQueue.status === EMAIL_QUEUE_STATUS.succeeded || emailQueue.status === EMAIL_QUEUE_STATUS.failed || emailQueue.status === EMAIL_QUEUE_STATUS.cancelled)" @click="resendEmail" class="btn btn-outline-warning">
            <font-awesome-icon :icon="['fas', 'envelope']" />
            {{ $t('action.resendEmail') }}
          </button>
        </div>
        <div class="btn-group d-none d-lg-block">
          <button v-if="userSettingsStore.data.developerMode" @click="viewHtmlMail()" class="btn btn-outline-secondary btn-sm">
            <font-awesome-icon :icon="['fas', 'eye']" />
            {{ $t('action.viewMail') || 'View Mail' }}
          </button>
          <button v-if="userSettingsStore.data.developerMode" @click="viewHtmlMail(i18n.locale.value)" class="btn btn-outline-secondary btn-sm" :title="$t('action.viewMailInMyLanguage') || 'View Mail in my language'">
            <font-awesome-icon :icon="['fas', 'eye']" />
            <font-awesome-icon :icon="['fas', 'language']" />
          </button>
          <button v-if="userSettingsStore.data.developerMode" @click="copyAsUrl(currentOrder.exposedOrderToken)" class="btn btn-outline-secondary btn-sm">
            <font-awesome-icon :icon="['fas', 'copy']" />
            {{ $t('field.generic.token') }}<br>
          </button>
        </div>
      </div>

      <!-- only small screen -->
      <div class="d-flex justify-content-between gap-3 align-items-center d-lg-none p-2">
        <div>
          <AuditItem :at="currentOrder.audit.createdAt" :by="currentOrder.audit.createdBy" />
        </div>
        <div class="btn-group">
          <button v-if="currentOrder.status === ORDER_HEAD_STATUS.pending || currentOrder.status === ORDER_HEAD_STATUS.inProgress" @click="cancelOrder" class="btn btn-outline-danger">
            <font-awesome-icon :icon="['fas', 'xmark']" />
            {{ $t('action.cancelOrderShort') }}
          </button>
          <button v-if="currentOrder.status === ORDER_HEAD_STATUS.pending && (!emailQueue || emailQueue.status === EMAIL_QUEUE_STATUS.succeeded || emailQueue.status === EMAIL_QUEUE_STATUS.failed || emailQueue.status === EMAIL_QUEUE_STATUS.cancelled)" @click="resendEmail" class="btn btn-outline-warning">
            <font-awesome-icon :icon="['fas', 'envelope']" />
            {{ $t('action.resendEmail') }}
          </button>
        </div>
      </div>

      <div v-if="(currentOrder.status === ORDER_HEAD_STATUS.inProgress || currentOrder.status === ORDER_HEAD_STATUS.completed) && currentOrder.isPartialDeliveryAny" class="mt-3 mb-3 px-sm-2">
        <div class="d-none d-lg-flex justify-content-start gap-3 mt-2">
          <OrderCompleteReason :reason="currentOrder.completeReason" />
          <template v-if="currentOrder.isPartialDelivery">
            <button @click="reorderMissingDeliveryQuantity" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'cart-shopping-fast']" class="me-2" />
              {{ $t('action.reorder') }}
            </button>
            <button @click="redraftUnderDeliveredItems" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              {{ $t('action.addToNewOrderEllipsis') }}
            </button>
            <button @click="ignoreMissingDeliveryQuantity" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'xmarks-lines']" class="me-2" />
              {{ $t('action.ignore') }}
            </button>
          </template>
        </div>
        <div class="d-flex d-lg-none flex-column gap-1 mx-2">
          <OrderCompleteReason :reason="currentOrder.completeReason" class="p-2" />
          <template v-if="currentOrder.isPartialDelivery">
            <button @click="reorderMissingDeliveryQuantity" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'cart-shopping-fast']" class="me-2" />
              {{ $t('action.reorder') }}
            </button>
            <button @click="redraftUnderDeliveredItems" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'plus']" class="me-2" />
              {{ $t('action.addToNewOrderEllipsis') }}
            </button>
            <button @click="ignoreMissingDeliveryQuantity" class="btn btn-outline-primary">
              <font-awesome-icon :icon="['fas', 'xmarks-lines']" class="me-2" />
              {{ $t('action.ignore') }}
            </button>
          </template>
        </div>
      </div>

      <OrderLineList class="px-sm-2" :order-id="currentOrder.id" :show-delivered-quantity="!currentOrder.supplier.receivesByDeliveryInterface && currentOrder.status === ORDER_HEAD_STATUS.completed" />
    </div>
    <div v-else>
      <span >{{ $t('error.noData') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import TranslationsService from "@/services/translations.ts";
import dayjs from 'dayjs';
import { router } from '@/services';
import { useUserSettingsStore, useOrdersStore, useEmailQueueStore } from '@/stores';
import emailService from '@/services/EmailService';
import OrderLineList from "@/components/orders/OrderLineList.vue";
import OrderCompleteReason from "@/components/orders/OrderCompleteReason.vue";
import AuditItem from "@/components/AuditItem.vue";
import OrderStatus from '@/components/orders/OrderStatus.vue';
import EmailQueueStatus from '@/components/EmailQueueStatus.vue';
import { EMAIL_QUEUE_STATUS } from '@/models/responses/IEmailQueue'
import { ORDER_HEAD_STATUS } from '@/models/Enums';
import IEmailQueue from '@/models/responses/IEmailQueue';
import Order from '@/models/frontend/Order';
import Supplier from '@/models/frontend/Supplier';

const i18n = useI18n();

const userSettingsStore = useUserSettingsStore();
const ordersStore = useOrdersStore();
const emailQueueStore = useEmailQueueStore();
const route = useRoute();

const props = defineProps({
  orderId: { type: Number, required: true }
});

let unsubscribeEmailChanged = () => { };
const currentOrder = ref<Order | undefined>(ordersStore.getById(props.orderId));
const currentSupplier = ref<Supplier | undefined>(undefined);
const emailQueue = ref<IEmailQueue | undefined>();
const emailQueueStatusTitle = computed(() => {
  if (emailQueue.value) {
    const relativeAge = dayjs(emailQueue.value.updatedAt).fromNow();
    const absoluteAge = i18n.d(emailQueue.value.updatedAt, 'dateandtime');
    return `${relativeAge} (${absoluteAge})`;
  }
  return '';
});

const loadOrderData = async (orderId: number) => {
  await ordersStore.fetch(orderId);
  currentOrder.value = ordersStore.getById(orderId);
  if (currentOrder.value == undefined) {
    toast.error(i18n.t('page.order.notFoundError'));
    return;
  }
  currentSupplier.value = currentOrder.value.supplier;
  if (currentSupplier.value == undefined) {
    toast.error(i18n.t('page.order.supplierNotFoundError'));
    return;
  }
  
  // Load email queue asynchronously without blocking
  loadEmailQueue();
};

onMounted(async () => {
  await loadOrderData(props.orderId);
});

// Watch for route parameter changes and reload data when orderId changes
watch(() => route.params.orderId, async (newOrderId) => {
  if (newOrderId && (!currentOrder.value || Number(newOrderId) !== currentOrder.value.id)) {
    await loadOrderData(Number(newOrderId));
  }
}, { immediate: true });

onUnmounted(() => {
  unsubscribeEmailChanged();
});

// Separate function to load email queue asynchronously
const loadEmailQueue = async () => {
  if (currentOrder.value) {
    emailQueue.value = await emailQueueStore.load(`order:${currentOrder.value.id}`);
    unsubscribeEmailChanged(); // unsubscribe from previous event
    unsubscribeEmailChanged = emailService.onEmailChanged(async (id: number) => {
      if (id === emailQueue.value?.id && currentOrder.value) {
        emailQueue.value = await emailQueueStore.load(`order:${currentOrder.value.id}`);
      }
    });
  }
};

const resendEmail = async () => {
  if (currentOrder.value) {
    await ordersStore.resend(currentOrder.value.id,
      async () => {
        if (currentOrder.value) {
          emailQueue.value = await emailQueueStore.load(`order:${currentOrder.value.id}`);
        }
      },
      () => { }
    );
  }
}

const cancelOrder = async () => {
  if (currentOrder.value) {
    await ordersStore.cancel(currentOrder.value.id, onCancelFulfilled, onRejected);
    toast.success(i18n.t('statement.orderIsCancelled'), { autoClose: 1000 });
  }
}

const redraftUnderDeliveredItems = async () => {
  if (currentOrder.value) {
    await ordersStore.redraft(currentOrder.value.id, onRedraftFulfilled, onRejected);
    toast.success(i18n.t('statement.partialDeliveryMissingItemsCopiedToDraftOrder'), { autoClose: 1000 });
  }
}

const reorderMissingDeliveryQuantity = async () => {
  if (currentOrder.value) {
    await ordersStore.reorder(currentOrder.value.id, onReorderFulfilled, onRejected);
    toast.success(i18n.t('statement.partialDeliveryMissingItemsReordered'), { autoClose: 1000 });
  }
}

const ignoreMissingDeliveryQuantity = async () => {
  if (currentOrder.value) {
    await ordersStore.ignore(currentOrder.value.id, onIgnoreFulfilled, onRejected);
    toast.success(i18n.t('statement.partialDeliveryIgnored'), { autoClose: 1000 });
  }
}

const copyAsUrl = (data: string) => {
  const baseUrl = window.location.origin;
  const orderId = currentOrder.value?.id;
  const url = `${baseUrl}/transaction/orders/${orderId}/accept#token=${data}`;
  copyToClipboardWithMessage(url, i18n.t('statement.contentCopied'));
}

const viewHtmlMail = async (culture?: string) => {
  if (currentOrder.value) {
    try {
      const htmlContent = await ordersStore.getHtmlMail(currentOrder.value.id, culture);
      if (htmlContent) {
        // Create a blob with the HTML content
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        // Open in a new tab
        window.open(url, '_blank');
        
        // Clean up the object URL after a delay
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else {
        toast.error(i18n.t('error.noHtmlMailAvailable') || 'No HTML mail available', { autoClose: 5000 });
      }
    } catch (error) {
      toast.error(i18n.t('error.failedToLoadHtmlMail') || 'Failed to load HTML mail', { autoClose: 5000 });
    }
  }
}

function copyToClipboardWithMessage(clipboardContent: string, message: string) {
  navigator.clipboard.writeText(clipboardContent).then(
    () => toast.success(message, { autoClose: 1000 }),
    () => toast.error(i18n.t('error.copiedError'), { autoClose: 1000 })
  );
}

function onCancelFulfilled(): void | PromiseLike<void> {
  if (!currentOrder.value) return;
  // Refresh the current order after cancellation
  currentOrder.value = ordersStore.getById(currentOrder.value?.id);
}

function onReorderFulfilled(order: Order): void | PromiseLike<void> {
  router.push({ name: 'order', params: { orderId: order.id } });
}

function onRedraftFulfilled(): void | PromiseLike<void> {
  router.push({ name: 'order-create', params: { orderId: currentOrder.value?.id } });
}

function onIgnoreFulfilled(order: Order): void | PromiseLike<void> {
  router.push({ name: 'order', params: { orderId: order.id } });
}

function onRejected(reason: any): void | PromiseLike<void> {
  toast.error(i18n.t('error.apiError', { error: reason.message }), { autoClose: 10000 });
}
</script>
